/**
 * NEW demo mode (Demo 2.0) — a public, fully-simulated run of the REAL agency
 * portal at `/demo-new`, forked from the frozen `/demo` that clients already
 * hold links to. When active:
 *   - a fake session is seeded into Redux so route guards pass (see demo-seed),
 *   - a global fetch interceptor returns mock fixtures (see demo-backend),
 *   - the visitor chooses a guided tour or free exploration from the welcome.
 *
 * Mode is determined PURELY by the URL: the whole app runs under the
 * `/demo-new` basename, so the browser path is always `/demo-new/...` while in
 * this demo. `/demo-new` deliberately does NOT match the old demo's
 * `isDemoMode()` ('/demo-new'.startsWith('/demo/') is false), so the two demos
 * never cross-activate.
 */
const MODE_KEY = 'tara_demo_new_mode';
const WELCOME_KEY = 'tara_demo_new_welcomed';
const TOUR_START_KEY = 'tara_demo_new_tour_start';
const TOUR_DONE_KEY = 'tara_demo_new_tour_done';

const safeSession = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** True when the current URL is under /demo-new — i.e. running the NEW demo. */
export const isNewDemoMode = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname || '';
    return path === '/demo-new' || path.startsWith('/demo-new/');
  } catch {
    return false;
  }
};

export const enableNewDemoMode = (): void => {
  safeSession()?.setItem(MODE_KEY, '1');
};

export const disableNewDemoMode = (): void => {
  const s = safeSession();
  s?.removeItem(MODE_KEY);
  s?.removeItem(WELCOME_KEY);
  s?.removeItem(TOUR_START_KEY);
  s?.removeItem(TOUR_DONE_KEY);
};

/* ---- one-time welcome modal ---- */

/** Whether the welcome modal has already been shown this session. */
export const hasWelcomed = (): boolean =>
  safeSession()?.getItem(WELCOME_KEY) === '1';

export const markWelcomed = (): void => {
  safeSession()?.setItem(WELCOME_KEY, '1');
};

/* ---- one-shot signal from the welcome modal to start the guided tour ---- */

/** Welcome modal asks the tour to auto-start once the dashboard mounts. */
export const requestTourStart = (): void => {
  safeSession()?.setItem(TOUR_START_KEY, '1');
};

/** Tour reads + clears the start request (fires at most once per request). */
export const consumeTourStart = (): boolean => {
  const s = safeSession();
  if (!s) return false;
  const wanted = s.getItem(TOUR_START_KEY) === '1';
  if (wanted) s.removeItem(TOUR_START_KEY);
  return wanted;
};

/** Mark the guided tour as completed (so we don't auto-restart on free roam). */
export const markTourDone = (): void => {
  safeSession()?.setItem(TOUR_DONE_KEY, '1');
};

export const hasTourRun = (): boolean =>
  safeSession()?.getItem(TOUR_DONE_KEY) === '1';
